/**
 * Pure Marketplace view logic: status chips, card actions, scope rollups,
 * money and date copy. Kept free of React so it is unit tested directly.
 */
import type { Tone } from '@velora/ui';
import {
  MARKETPLACE_CATALOG_BY_KEY,
  type CatalogModule,
  type MarketplaceModuleKey,
  type OfficeModuleState,
  type OfficeModuleStatus,
  type PmsSystem,
  type Recommendation,
  type StoreModule,
  type WritebackLevel,
  type StoreOffice,
  type StoreResponse,
} from '@velora/contracts/marketplace';

export const DAY_MS = 86_400_000;

/* ---------- money and dates ---------- */

/** `$149` for whole dollars, `$31.61` otherwise. */
export function money(cents: number, opts: { cents?: boolean } = {}): string {
  const whole = cents % 100 === 0 && opts.cents !== true;
  const v = Math.abs(cents) / 100;
  const s = v.toLocaleString('en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  });
  return `${cents < 0 ? '-' : ''}$${s}`;
}

/** Hundredths of a cent per unit as copy: 200 -> "$0.02". */
export function unitPrice(milliCents: number): string {
  const dollars = milliCents / 10_000;
  return `$${dollars.toFixed(dollars < 0.1 ? 3 : 2).replace(/0$/, '')}`;
}

function toDate(value: string): Date {
  // A civil date (YYYY-MM-DD) is midday in the practice's zone, so it never
  // shifts a day when formatted.
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : new Date(value);
}

/** "Oct 23" (civil dates and instants, in the practice time zone). */
export function shortDate(value: string, timeZone: string): string {
  const civil = /^\d{4}-\d{2}-\d{2}$/.test(value);
  return toDate(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: civil ? 'UTC' : timeZone,
  });
}

/** "Oct 23, 2026". */
export function longDate(value: string, timeZone: string): string {
  const civil = /^\d{4}-\d{2}-\d{2}$/.test(value);
  return toDate(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: civil ? 'UTC' : timeZone,
  });
}

/** Whole days from `now` until `iso`, never negative. */
export function daysUntil(iso: string, now: number): number {
  return Math.max(0, Math.ceil((Date.parse(iso) - now) / DAY_MS));
}

/** Last day of access for an exclusive end instant ("access until Oct 31"). */
export function lastDay(isoExclusive: string): string {
  return new Date(Date.parse(isoExclusive) - 1).toISOString();
}

/* ---------- catalog ---------- */

export function catalog(key: string): CatalogModule | null {
  return (MARKETPLACE_CATALOG_BY_KEY as Record<string, CatalogModule | undefined>)[key] ?? null;
}

/**
 * Catalog list prices are placeholders until the owner sets them
 * (`pricing.placeholder`); every list price the Store shows says so.
 */
export const PLACEHOLDER_PRICE_LABEL = 'Placeholder';
export const PLACEHOLDER_PRICE_TITLE =
  'Placeholder list price: Velora has not set this price yet. Nothing is billed during the beta.';

export function priceLine(m: CatalogModule): string {
  return `${money(m.priceCents)}/office/mo`;
}

/** "3,000 texts included, then $0.02 each". */
export function usageLine(m: CatalogModule): string | null {
  if (!m.usage) return null;
  return `${m.usage.includedPerOffice.toLocaleString('en-US')} ${lowerLabel(m.usage.label)} included per office, then ${unitPrice(m.usage.overageMilliCents)} per ${m.usage.unit}`;
}

/** "Texts" -> "texts", but "AI minutes" stays (an acronym leads). */
export function lowerLabel(label: string): string {
  return /^[A-Z]{2}/.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1);
}

/** Whether any of the module's data writes back to the PMS. */
export function writesBack(m: CatalogModule): boolean {
  return m.dataAccess.some((r) => r.writeback.length > 0);
}

/** The data-access checkbox is required when the module writes to the PMS or PHI leaves Velora. */
export function needsAck(m: CatalogModule): boolean {
  return m.phiLeavesVelora || writesBack(m);
}

/* ---------- per-office status ---------- */

export const ON_STATUSES: ReadonlySet<OfficeModuleStatus> = new Set([
  'included',
  'trialing',
  'active',
  'scheduled_cancel',
  'past_due',
]);

/** Shown in Manage: anything an admin can still act on. */
export const MANAGEABLE: ReadonlySet<OfficeModuleStatus> = new Set([
  'trialing',
  'active',
  'scheduled_cancel',
  'past_due',
  'read_only',
]);

export interface ChipView {
  tone: Tone;
  label: string;
}

/** The status chip for one office (marketplace.md 4.2). Null when there is nothing to say. */
export function officeChip(s: OfficeModuleState, timeZone: string, now: number): ChipView | null {
  switch (s.status) {
    case 'included':
      return { tone: 'info', label: 'Included' };
    case 'trialing': {
      const d = s.trialEndsAt ? daysUntil(s.trialEndsAt, now) : null;
      return {
        tone: 'brand',
        label: d === null ? 'Trial' : d <= 1 ? 'Trial ends today' : `Trial, ${d} days left`,
      };
    }
    case 'active':
      return { tone: 'success', label: 'Active' };
    case 'scheduled_cancel':
      return {
        tone: 'warning',
        label: s.cancelAt ? `On until ${shortDate(lastDay(s.cancelAt), timeZone)}` : 'Turning off',
      };
    case 'past_due':
      return { tone: 'danger', label: 'Past due' };
    case 'read_only':
      return {
        tone: 'neutral',
        label: s.readOnlyEndsAt
          ? `Read-only until ${shortDate(s.readOnlyEndsAt, timeZone)}`
          : 'Read-only',
      };
    case 'paused':
      return { tone: 'neutral', label: s.reason === 'trial_ended' ? 'Trial ended' : 'Paused' };
    case 'canceled':
      return { tone: 'neutral', label: 'Off' };
    case 'pending':
      return { tone: 'neutral', label: 'Pending' };
    case 'available':
      return s.request
        ? { tone: 'neutral', label: `Requested by ${firstName(s.request.requestedBy)}` }
        : null;
  }
}

function firstName(name: string): string {
  return name.split(/\s+/)[0] ?? name;
}

export interface ScopeSummary {
  /** Offices in scope, in store order. */
  states: OfficeModuleState[];
  on: number;
  trialing: number;
  paid: number;
  trialAvailable: boolean;
  anyManageable: boolean;
  anyPastDue: boolean;
  /** Offices where it can be turned on (not on, not past due). */
  canTurnOn: number;
  requested: boolean;
  chip: ChipView | null;
}

export function scopeSummary(
  module: StoreModule,
  scopeIds: readonly string[],
  timeZone: string,
  now: number,
): ScopeSummary {
  const states = module.offices.filter((o) => scopeIds.includes(o.locationId));
  const on = states.filter((s) => ON_STATUSES.has(s.status)).length;
  const trialing = states.filter((s) => s.status === 'trialing').length;
  const paid = states.filter(
    (s) => s.status === 'active' || s.status === 'scheduled_cancel' || s.status === 'past_due',
  ).length;
  const anyPastDue = states.some((s) => s.status === 'past_due' || s.status === 'read_only');
  const canTurnOn =
    states.filter(
      (s) => !ON_STATUSES.has(s.status) && s.status !== 'read_only' && s.status !== 'past_due',
    ).length + trialing;
  let chip: ChipView | null = null;
  if (states.length === 1 && states[0]) chip = officeChip(states[0], timeZone, now);
  else if (anyPastDue) chip = { tone: 'danger', label: 'Past due' };
  else if (on > 0) {
    chip =
      trialing === on
        ? { tone: 'brand', label: `Trial at ${on} of ${states.length}` }
        : { tone: 'success', label: `On at ${on} of ${states.length}` };
  } else {
    const req = states.find((s) => s.request);
    if (req?.request)
      chip = { tone: 'neutral', label: `Requested by ${firstName(req.request.requestedBy)}` };
  }
  return {
    states,
    on,
    trialing,
    paid,
    trialAvailable: states.some((s) => s.trialAvailable),
    anyManageable: states.some((s) => MANAGEABLE.has(s.status)),
    anyPastDue,
    canTurnOn,
    requested: states.some((s) => s.request !== null),
    chip,
  };
}

export type CardAction = 'manage' | 'trial' | 'turn-on' | 'request' | 'requested' | 'open';

/** The one action a card shows on hover and focus (marketplace.md 4.2). */
export function cardAction(summary: ScopeSummary, canPurchase: boolean): CardAction {
  if (canPurchase) {
    if (summary.anyManageable && summary.canTurnOn === summary.trialing) return 'manage';
    if (summary.trialAvailable) return 'trial';
    if (summary.canTurnOn > 0) return 'turn-on';
    return summary.anyManageable ? 'manage' : 'open';
  }
  if (summary.on > 0) return 'open';
  return summary.requested ? 'requested' : 'request';
}

export const CARD_ACTION_LABEL: Record<CardAction, string> = {
  manage: 'Manage',
  trial: 'Try free',
  'turn-on': 'Turn on',
  request: 'Request',
  requested: 'Requested',
  open: 'Details',
};

/* ---------- store filters ---------- */

export type StatusFilter = 'all' | 'on' | 'trials' | 'available';

export function matchesFilter(summary: ScopeSummary, f: StatusFilter): boolean {
  switch (f) {
    case 'all':
      return true;
    case 'on':
      return (
        summary.on - summary.trialing > 0 || summary.states.some((s) => s.status === 'included')
      );
    case 'trials':
      return summary.trialing > 0;
    case 'available':
      return summary.on === 0;
  }
}

export function matchesQuery(m: CatalogModule, q: string): boolean {
  const query = q.trim().toLowerCase();
  if (!query) return true;
  const hay = [m.name, m.valueLine, m.description, ...m.features.map((f) => f.title)]
    .join(' ')
    .toLowerCase();
  return query.split(/\s+/).every((w) => hay.includes(w));
}

/** Monthly spend for paid modules in scope (list price per paying office). */
export function monthlySpend(store: StoreResponse, scopeIds: readonly string[]): number {
  let total = 0;
  for (const m of store.modules) {
    const c = catalog(m.key);
    if (!c) continue;
    for (const s of m.offices) {
      if (!scopeIds.includes(s.locationId)) continue;
      if (s.status === 'active' || s.status === 'past_due') total += s.priceCents ?? c.priceCents;
    }
  }
  return total;
}

export function officeName(offices: readonly StoreOffice[], id: string): string {
  return offices.find((o) => o.id === id)?.name ?? 'Office';
}

export function moduleKeyOf(value: string): MarketplaceModuleKey | null {
  return catalog(value)?.key ?? null;
}

/** The civil date before `isoDate` (an exclusive period end as a last day). */
export function dayBefore(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/** Invoice line copy with ISO dates as "Nov 1" ("Analytics Pro - Saginaw (Nov 1 to Nov 30)"). */
export function prettyLine(description: string, timeZone: string): string {
  return description.replace(/\d{4}-\d{2}-\d{2}/g, (d) => shortDate(d, timeZone));
}

/* ---------- PMS badge row (marketplace.md 4.2, AT1) ---------- */

export interface PmsSupport {
  pms: PmsSystem;
  level: WritebackLevel;
}

const PMS_ORDER: readonly PmsSystem[] = ['opendental', 'dentrix', 'eaglesoft'];

/**
 * Write-back per PMS across the offices in scope that run it. Offices on the
 * same PMS with different connectors (mock and PulseSync v1) roll up to
 * `partial` unless they agree.
 */
export function pmsSupport(
  sm: StoreModule,
  offices: readonly StoreOffice[],
  scopeIds: readonly string[],
): PmsSupport[] {
  const by = new Map<PmsSystem, Set<WritebackLevel>>();
  for (const o of offices) {
    if (!o.pmsSystem || !scopeIds.includes(o.id)) continue;
    const level = sm.offices.find((s) => s.locationId === o.id)?.writeback;
    if (!level) continue;
    const set = by.get(o.pmsSystem) ?? new Set<WritebackLevel>();
    set.add(level);
    by.set(o.pmsSystem, set);
  }
  return PMS_ORDER.filter((p) => by.has(p)).map((pms) => {
    const levels = [...(by.get(pms) ?? [])];
    return { pms, level: levels.length === 1 ? (levels[0] as WritebackLevel) : 'partial' };
  });
}

export const WRITEBACK_BADGE: Record<WritebackLevel, string> = {
  full: 'write-back',
  partial: 'partial write-back',
  none: 'no write-back yet',
  not_needed: 'reads only',
};

/**
 * The hero recommendation: the first one (strongest signal first) for an
 * office in scope where the module is not on. Another office having the
 * module never hides it (marketplace.md AT2).
 */
export function pickRecommendation(
  recs: readonly Recommendation[],
  modules: readonly StoreModule[],
  scopeIds: readonly string[],
): Recommendation | null {
  for (const r of recs) {
    const sm = modules.find((x) => x.key === r.moduleKey);
    if (!sm) continue;
    const offices = r.locationId ? [r.locationId] : scopeIds;
    if (!offices.every((id) => scopeIds.includes(id))) continue;
    const states = sm.offices.filter((s) => offices.includes(s.locationId));
    if (states.length > 0 && states.every((s) => !ON_STATUSES.has(s.status))) return r;
  }
  return null;
}

/** "November" for a YYYY-MM-DD date (the month a billing period covers). */
export function monthName(isoDate: string): string {
  const [y, m] = isoDate.split('-').map(Number) as [number, number];
  return new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, 1)),
  );
}

/** Day of a trial for a date, the start day being day 1 (MK-08 reminders on days 7 and 11). */
export function trialDayOf(startsOn: string, isoDate: string): number {
  const d = (s: string) => {
    const [y, m, day] = s.split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, day);
  };
  return Math.round((d(isoDate) - d(startsOn)) / 86_400_000) + 1;
}

/**
 * Marketplace billing is live only when the server says so (`billingLive`:
 * a real Square rail plus `VELORA_MARKETPLACE_BILLING=1`). Anything else,
 * including an older server that does not send the field, is the beta
 * no-billing hold: modules are comped and no invoice amount is shown.
 */
export function isComped(data: { billingLive?: boolean | undefined } | undefined): boolean {
  return data?.billingLive !== true;
}

export const COMPED_LABEL = 'Comped (beta): not billed';
